import { ListItem } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useDispatch } from 'react-redux';
// eslint-disable-next-line import/no-unresolved
import DateFormatService from '../../../services/DateFormatService';
import { DatedCard } from '../../../components';
import { getSequentialBannerProps } from '../../../Enums/Helpers/StylingHelper';
import TimeService from '../../../services/TimeService';
import {
    EmployeeBidProfileColumn,
    EmployeeBiddingStatusNames,
    BidProfileStatusNames,
    UkgDateFormat,
    BidProfileStatus,
    EmployeeBiddingStatus,
    BidProcessType,
} from '../../../Enums';
import { selectedBiddableProfile, selectedBiddedProfile } from '../../store/bidProfile';

/**
 * @param {EmployeeBidProfile.IEmployeeBidProfile} bidProfile
 * @param {Function} t
 * @returns {string}
 */
function getClosedCardSubtitleLabel(bidProfile, t) {
    const {
        signOffDate, cancelationDate, lastUpdate, employeeSubmissionDate
    } = bidProfile;
    const isBidSubmittedOrDecline = [EmployeeBiddingStatus.Submitted, EmployeeBiddingStatus.Declined]
        .includes(bidProfile.employeeSubmissionStatus);
    let closedLabelDate = null;
    if (isBidSubmittedOrDecline) {
        closedLabelDate = employeeSubmissionDate;
    } else if (bidProfile.status === BidProfileStatus.Published) {
        closedLabelDate = signOffDate;
    } else if (bidProfile.status === BidProfileStatus.Canceled) {
        closedLabelDate = cancelationDate;
    } else {
        closedLabelDate = lastUpdate;
    }
    const closedCardSubtitleLabel = `${t('on')} ${DateFormatService.formatDateOrEmpty(closedLabelDate, UkgDateFormat.LongDateTime)}`;
    return closedCardSubtitleLabel;
}
/**
     * @param {{
     *      profile: EmployeeBidProfile.IEmployeeBidProfile|,
     *      employeeBidProfileColumn:EmployeeBidProfileColumn,
     * }} param0
     * @returns
     */
const BidCard = ({ profile, employeeBidProfileColumn }) => {
    const dispatch = useDispatch();
    const { t: tBidCard } = useTranslation('shiftBidding', { keyPrefix: 'employee.bidSelection.bidCard' });
    const { t: tCommon } = useTranslation('shiftBidding', { keyPrefix: 'common' });
    let datedCardSubtitle = '';
    let datedCardSubtitleColor = '#357ebd';
    let datedCardSubtitleLine2 = '';
    let datedCardLastSavedOn = '';
    let displaySummary = false;
    const isSequential = profile.bidProcessType === BidProcessType.Sequential;
    const isAvailable = EmployeeBidProfileColumn.Available === employeeBidProfileColumn;
    const isCanceled = profile.status === BidProfileStatus.Canceled;
    const EmployeeBiddingStatusName = EmployeeBiddingStatusNames[profile.employeeSubmissionStatus];
    const BidProfileStatusName = BidProfileStatusNames[profile.status];
    const statusName = isAvailable || isCanceled ? BidProfileStatusName : EmployeeBiddingStatusName;
    /**
      * @param {string} profileId
    */
    const handleSelectedBidProfile = (profileId) => {
        if (displaySummary) {
            dispatch(selectedBiddedProfile(profileId));
        } else {
            dispatch(selectedBiddableProfile(profileId));
        }
    };
    /** @param {Date}  submissionPeriodStartDate */
    const getStartingSubtitle = (submissionPeriodStartDate) => {
        const daysDifference = TimeService.getTimeDiffFromNowInDays(submissionPeriodStartDate);
        return daysDifference === 0
            ? tBidCard('startsToday')
            : tBidCard('startsInDate', { startDate: daysDifference, multiple: daysDifference > 1 ? 's' : '' });
    };
    /** @param {Date}  submissionPeriodEndDate */
    const getEndingSubtitle = (submissionPeriodEndDate) => {
        const daysDifference = TimeService.getTimeDiffFromNowInDays(submissionPeriodEndDate);
        return daysDifference === 0
            ? tBidCard('closesToday')
            : tBidCard('endsInDate', { endDate: daysDifference, multiple: daysDifference > 1 ? 's' : '' });
    };

    switch (employeeBidProfileColumn) {
        case EmployeeBidProfileColumn.Available:
            if (profile.status === BidProfileStatus.Scheduled) {
                datedCardSubtitle = getStartingSubtitle(profile.submissionPeriodStartDate);
            } else {
                datedCardSubtitle = getEndingSubtitle(profile.submissionPeriodEndDate);
                datedCardSubtitleColor = '#F13F3F';
            }
            break;
        case EmployeeBidProfileColumn.Draft: {
            datedCardSubtitle = getEndingSubtitle(profile.submissionPeriodEndDate);
            datedCardSubtitleColor = '#F13F3F';
            datedCardLastSavedOn = tBidCard(
                'lastSavedOnDate',
                { lastSaveDate: DateFormatService.formatDateOrEmpty(profile.employeeSubmissionDate, UkgDateFormat.LongDateTime) },
            );
            break;
        }
        case EmployeeBidProfileColumn.Submitted: {
            datedCardSubtitle = '';
            datedCardSubtitleColor = '#F13F3F';
            datedCardSubtitleLine2 = `
                ${tBidCard('on')} ${DateFormatService.formatDateOrEmpty(profile.employeeSubmissionDate, UkgDateFormat.LongDateTime)}`;
            displaySummary = true;
            break;
        }
        case EmployeeBidProfileColumn.Closed:
            datedCardSubtitleLine2 = getClosedCardSubtitleLabel(profile, tBidCard);
            displaySummary = true;
            break;
        default:
            break;
    }

    return (
        <ListItem
            className="bid-profile-item"
            key={`ListItem_Scheduled_bidProfileItem_${profile.profileId} `}
        >
            <DatedCard
                title={profile.name}
                subtitle={datedCardSubtitle}
                subtitleColor={datedCardSubtitleColor}
                subtitleLine2={datedCardSubtitleLine2}
                note={`${DateFormatService.formatDateOrEmpty(profile.submissionPeriodStartDate, UkgDateFormat.LongDateTime)}
                - ${DateFormatService.formatDateOrEmpty(profile.submissionPeriodEndDate, UkgDateFormat.LongDateTime)} `}
                date={moment(profile.submissionPeriodStartDate)}
                onClick={() => handleSelectedBidProfile(profile.profileId)}
                chipLabel={profile.isRebidAllowed ? tCommon('rebidNumber', { number: profile.rebidNumber }) : ''}
                isShowProtectedIcon={!!profile.approvedShiftlineInfos?.isLocked}
                isLocked={!!profile.approvedShiftlineInfos?.isLocked}
                statusName={statusName}
                lastSavedOn={datedCardLastSavedOn}
                sequentialBannerProps={isSequential ? getSequentialBannerProps(profile) : undefined}
                employeeAddedAfterClose={profile.employeeSequentialSchedule?.isAddedWhenClosed}
                employeeSubmissionStartDate={profile.employeeSequentialSchedule?.employeeSubmissionStartDate}
            />

        </ListItem>
    );
};
export default BidCard;
